import type { Fixture } from '@h2h/shared';
import { AppShell, Content } from '../../components/ui/AppShell';
import { DesktopColumns } from '../../components/ui/DesktopColumns';
import { ArrowRight, Info, Swords, Trophy } from '../../components/ui/icons';
import { useOpenProfile } from '../../hooks/useOpenProfile';
import type { DuelDetail, DuelSide } from '../../lib/api';
import { cn } from '../../lib/cn';
import { countdownTo, initials } from '../../lib/format';
import { BackBar } from './BackBar';
import { Combatant } from './Combatant';
import { DuelByDuel } from './CyclingDuelCard';
import { PickByPick } from './FootballPickCard';
import { CYC_MAX_WEIGHT, pickPoints } from './helpers';

export function ActiveDuelView({
  data,
  me,
  rival,
  myWins,
  theirWins,
  itemIds,
  fixtureMap,
  myDuelById,
  rivalDuelById,
  remaining,
  showItsOn,
  iHaveEntry,
  stageStarted,
  onBack,
  onGoToPicks,
  onOpenFixture
}: {
  data: DuelDetail;
  me: DuelSide;
  rival: DuelSide;
  myWins: number;
  theirWins: number;
  itemIds: string[];
  fixtureMap: Map<string, Fixture>;
  myDuelById: Map<string, NonNullable<DuelSide['duelPicks']>[number]>;
  rivalDuelById: Map<string, NonNullable<DuelSide['duelPicks']>[number]>;
  remaining: number;
  showItsOn: boolean;
  iHaveEntry: boolean;
  stageStarted: boolean;
  onBack: () => void;
  onGoToPicks: () => void;
  onOpenFixture: (fixtureId: string) => void;
}) {
  const openProfile = useOpenProfile();
  const { duel, round } = data;
  const isCycling = data.sport === 'CYCLING';
  const unitSingular = isCycling ? 'duel' : 'match';
  const unitPlural = isCycling ? 'duels' : 'matches';

  const lead = me.score - rival.score;
  const total = Math.max(1, Math.abs(me.score) + Math.abs(rival.score));
  const myShare = Math.round((Math.max(0, me.score) / total) * 100);
  const resolved = duel.status === 'RESOLVED';

  return (
    <AppShell>
      <Content width="wide">
        <BackBar
          onBack={onBack}
          label="Friends"
          eyebrow={`${round?.label ?? 'Round'} · Head-to-head duel`}
        />

        <DesktopColumns
          railFirst
          asidePosition="left"
          asideWidth="400px"
          aside={
            <>
        {/* VS hero */}
        <div className="relative mt-3 overflow-hidden rounded-[30px] bg-gradient-to-br from-[#1C1C24] to-[#0A0A0E] px-5 pb-5 pt-[22px] shadow-[0_24px_46px_-18px_rgba(0,0,0,0.6)]">
          <span className="pointer-events-none absolute -left-[30px] -top-[50px] h-[200px] w-[200px] rounded-full bg-[radial-gradient(circle,rgba(91,110,225,0.28),transparent_70%)]" />
          <span className="pointer-events-none absolute -right-[30px] -top-[50px] h-[200px] w-[200px] rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.22),transparent_70%)]" />
          <div className="relative text-center">
            <span className="text-[10px] font-bold uppercase tracking-eyebrow text-[#9A9AA4]">
              {round?.label ?? 'Round'} · {isCycling ? 'same duels' : 'same fixtures'}
            </span>
            <div className="mt-4 flex items-start justify-between">
              <Combatant
                name="You"
                tone={0}
                score={me.score}
                you
                initialsText={initials(me.displayName)}
                onClick={() => openProfile(undefined, true)}
              />
              <div className="flex flex-col items-center gap-[6px] pt-[14px]">
                <div className="grid h-[42px] w-[42px] rotate-45 place-items-center rounded-[13px] border border-white/15 bg-white/[0.08]">
                  <span className="-rotate-45 text-[13px] font-extrabold text-white">VS</span>
                </div>
              </div>
              <Combatant
                name={rival.displayName}
                tone={rival.avatarTone}
                score={rival.score}
                initialsText={initials(rival.displayName)}
                onClick={() => openProfile(rival.userId)}
              />
            </div>

            {/* momentum bar */}
            <div className="mt-5 flex h-[7px] overflow-hidden rounded-[4px] bg-white/10">
              <div className="bg-gradient-to-r from-accent-deep to-accent" style={{ width: `${myShare}%` }} />
              <div className="bg-white/55" style={{ width: `${100 - myShare}%` }} />
            </div>
            <div className="mt-3 text-[12px] font-bold text-[#C4C4CC]">
              {resolved ? (
                lead > 0 ? (
                  <span className="text-accent">You won by {Math.abs(lead)}</span>
                ) : lead < 0 ? (
                  <span className="text-miss">Lost by {Math.abs(lead)}</span>
                ) : (
                  <>Drawn level</>
                )
              ) : lead === 0 ? (
                <>Level — {remaining} {remaining === 1 ? unitSingular : unitPlural} to play</>
              ) : (
                <>
                  {lead > 0 ? 'You lead' : 'Behind'} by{' '}
                  <span className={lead > 0 ? 'text-accent' : 'text-miss'}>{Math.abs(lead)}</span>
                  {remaining > 0 ? ` — ${remaining} to play` : ''}
                </>
              )}
            </div>
          </div>
        </div>

        {/* it's on — make your picks while the round is open */}
        {showItsOn && (
          <div className="mt-[14px] overflow-hidden rounded-2xl border border-accent/25 bg-accent/[0.07] p-4">
            <div className="flex items-center gap-[10px]">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-tile bg-accent/15">
                <Swords width={18} height={18} strokeWidth={2} className="text-accent-ink" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-extrabold tracking-snug text-ink">It’s on — duel accepted</div>
                <div className="text-[12px] font-medium text-ink2">
                  {iHaveEntry
                    ? isCycling
                      ? 'Your duels are in.'
                      : 'Your cards are in.'
                    : isCycling
                      ? 'Call your duels before the stage rolls out.'
                      : 'Build your cards before kickoff.'}
                  {round ? ` Locks in ${countdownTo(round.firstKickoffAt)}.` : ''}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onGoToPicks}
              className={cn(
                'tap mt-3 flex w-full items-center justify-center gap-[8px] rounded-[14px] py-[13px] text-[15px] font-extrabold',
                iHaveEntry ? 'border border-black/10 bg-card text-ink' : 'bg-accent text-white shadow-[0_12px_24px_-12px_rgba(10,150,110,0.7)]'
              )}
            >
              {iHaveEntry
                ? isCycling
                  ? 'Edit your duels'
                  : 'Edit your cards'
                : isCycling
                  ? 'Call your duels'
                  : 'Build your cards'}
              <ArrowRight width={16} height={16} strokeWidth={2.6} />
            </button>
          </div>
        )}

        {/* format explainer */}
        <div className="flex items-start gap-[9px] px-1 pt-[14px]">
          <Info width={15} height={15} strokeWidth={2} className="mt-[2px] shrink-0 text-ink2" />
          <span className="text-[12px] font-medium leading-[1.4] text-ink2">
            You and {rival.displayName} both play <span className="font-bold text-ink">{round?.label ?? 'this round'}</span>. Whoever
            scores more across their {isCycling ? 'duels' : 'cards'} wins the duel — same engine, same points.
          </span>
        </div>

        {/* season series */}
        {(myWins > 0 || theirWins > 0) && (
          <div className="mt-[14px] flex items-center gap-3 rounded-lg border border-black/5 bg-card p-[13px_15px] shadow-tile">
            <div className="grid h-9 w-9 place-items-center rounded-tile bg-fill">
              <Trophy width={19} height={19} strokeWidth={2} className="text-ink" />
            </div>
            <div className="flex-1">
              <div className="text-[13px] font-bold tracking-snug text-ink">Season series</div>
              <div className="text-[11px] font-medium text-ink2">
                Duels won vs {rival.displayName}
              </div>
            </div>
            <div className="flex items-center gap-[7px]">
              <span className="num text-[18px] font-extrabold text-ink">{myWins}</span>
              <span className="text-[13px] font-bold text-ink4">–</span>
              <span className="num text-[18px] font-extrabold text-[#5B6EE1]">{theirWins}</span>
            </div>
          </div>
        )}
            </>
          }
          main={
            <>
        {/* pick by pick */}
        <div className="pt-[22px] md:pt-1">
          <span className="text-[13px] font-bold tracking-snug text-ink">{isCycling ? 'Duel by duel' : 'Card by card'}</span>
        </div>
        <div className="flex flex-col gap-[10px] pt-3">
          {isCycling
            ? itemIds.map((id) => {
                const f = fixtureMap.get(id);
                const mine = myDuelById.get(id);
                const theirs = rivalDuelById.get(id);
                return (
                  <DuelByDuel
                    key={id}
                    fixture={f}
                    mine={mine}
                    theirs={theirs}
                    rivalName={rival.displayName}
                    banker={mine?.confidenceWeight === CYC_MAX_WEIGHT}
                    stageStarted={stageStarted}
                  />
                );
              })
            : itemIds.map((id) => {
                const f = fixtureMap.get(id);
                const myPick = me.predictions.find((p) => p.fixtureId === id);
                const rivalPick = rival.predictions.find((p) => p.fixtureId === id);
                return (
                  <PickByPick
                    key={id}
                    fixture={f}
                    mine={myPick}
                    theirs={rivalPick}
                    myPoints={myPick ? pickPoints(myPick, f) : null}
                    theirPoints={rivalPick ? pickPoints(rivalPick, f) : null}
                    rivalName={rival.displayName}
                    onOpen={f ? () => onOpenFixture(id) : undefined}
                  />
                );
              })}
        </div>
            </>
          }
        />
      </Content>
    </AppShell>
  );
}
